import { useState } from 'react';
import type { JSX } from 'react';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import type { PrefetchedDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type { Sharer } from 'src/app/pages/petitionGamma/details/shared/types';
import type { PetitionGammaCommonProps } from 'src/app/pages/petitionGamma/shared/types';

import { getPreviewLinkById } from '../../../api/getPreviewLinkById';
import { ShareHeadlineProvider, useShareForStartersShareHeadline } from '../../hooks/shareHeadline';
import { getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';

type PrefetchedData = Readonly<{
	sharer?: Sharer;
	previewLink: {
		id?: string;
		title?: string;
		description?: string;
	};
}>;

export const PetitionDetailsShareForStarters = withPrefetchedData(
	({ userInfoState, prefetchedData }: PetitionGammaCommonProps & PrefetchedDataProps<PrefetchedData>): JSX.Element => {
		// this allows us to conserve the sharer after first render
		const [sharer] = useState<Sharer | undefined>(prefetchedData?.sharer);
		const { shareChannel } = useKnownAvailableParams<{
			slug: string;
			shareChannel: string;
		}>();

		const { title, description } = useShareForStartersShareHeadline(shareChannel);

		if (!sharer) {
			return <CommonPetitionDetails userInfoState={userInfoState} />;
		}

		return (
			<ShareHeadlineProvider
				sharer={sharer}
				title={title}
				description={description}
				previewLink={prefetchedData?.previewLink.title ? prefetchedData.previewLink : undefined}
			>
				<CommonPetitionDetails userInfoState={userInfoState} />
			</ShareHeadlineProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [CommonPetitionDetails],
		prefetchData: async (context) => {
			const {
				params: { sharerUserId, slug, previewId },
			} = context;

			let previewLink = {};
			if (slug && previewId) {
				const previewLinkResult = await getPreviewLinkById(slug, context.utilityContext, 'PREVIEW_ID', previewId);
				if (previewLinkResult.previewLinks.length > 0) {
					previewLink = previewLinkResult.previewLinks[0] || {};
				}
			}
			if (!sharerUserId) return { previewLink };
			try {
				return { sharer: await getSharer(sharerUserId, context.utilityContext), previewLink };
			} catch (e) {
				return { previewLink };
			}
		},
	},
);
